import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { ScrollArea } from "@/components/ui/scroll-area";
import { MessageSquare, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { CommentThread } from "./CommentThread";
import { MentionInput } from "./MentionInput";

interface Comment {
  id: string;
  content: string;
  user_id: string;
  created_at: string;
  updated_at: string;
  parent_id: string | null;
  mentions: string[] | null;
  user?: {
    full_name: string | null;
    avatar_url: string | null;
  };
  replies?: Comment[];
}

interface RecordCommentsProps {
  recordId: string;
  workspaceId: string;
}

export const RecordComments = ({ recordId, workspaceId }: RecordCommentsProps) => {
  const { user } = useAuth();
  const [comments, setComments] = useState<Comment[]>([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [newComment, setNewComment] = useState("");
  const [newMentions, setNewMentions] = useState<string[]>([]);

  useEffect(() => {
    if (recordId) {
      fetchComments();
      subscribeToComments();
    }
  }, [recordId]);

  const fetchComments = async () => {
    try {
      const { data, error } = await supabase
        .from("comments")
        .select(`
          *,
          user:user_id (
            full_name,
            avatar_url
          )
        `)
        .eq("record_id", recordId)
        .order("created_at", { ascending: true });

      if (error) throw error;

      // Build comment tree
      const commentMap = new Map<string, Comment>();
      const rootComments: Comment[] = [];

      (data || []).forEach((comment: any) => {
        commentMap.set(comment.id, { ...comment, replies: [] });
      });

      commentMap.forEach((comment) => {
        if (comment.parent_id) {
          const parent = commentMap.get(comment.parent_id);
          if (parent) {
            parent.replies = parent.replies || [];
            parent.replies.push(comment);
          }
        } else {
          rootComments.push(comment);
        }
      });

      setComments(rootComments);
    } catch (error: any) {
      console.error("Failed to fetch comments:", error);
      toast.error("Failed to load comments");
    } finally {
      setLoading(false);
    }
  };

  const subscribeToComments = () => {
    const channel = supabase
      .channel(`comments-${recordId}`)
      .on(
        "postgres_changes",
        {
          event: "*",
          schema: "public",
          table: "comments",
          filter: `record_id=eq.${recordId}`,
        },
        (payload) => {
          if (payload.eventType === "INSERT" || payload.eventType === "UPDATE") {
            fetchComments();
          } else if (payload.eventType === "DELETE") {
            fetchComments();
          }
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  };

  const handleSubmit = async () => {
    if (!newComment.trim() || !user) return;

    setSubmitting(true);
    try {
      const { error } = await supabase.from("comments").insert({
        record_id: recordId,
        user_id: user.id,
        content: newComment.trim(),
        mentions: newMentions.length > 0 ? newMentions : null,
      });

      if (error) throw error;

      setNewComment("");
      setNewMentions([]);
      toast.success("Comment added");

      // Notify mentioned users
      if (newMentions.length > 0) {
        await notifyMentions(newMentions);
      }
    } catch (error: any) {
      console.error("Failed to add comment:", error);
      toast.error("Failed to add comment");
    } finally {
      setSubmitting(false);
    }
  };

  const handleReply = async (parentId: string, content: string, mentions: string[]) => {
    if (!content.trim() || !user) return;

    try {
      const { error } = await supabase.from("comments").insert({
        record_id: recordId,
        user_id: user.id,
        parent_id: parentId,
        content: content.trim(),
        mentions: mentions.length > 0 ? mentions : null,
      });

      if (error) throw error;

      if (mentions.length > 0) {
        await notifyMentions(mentions);
      }
    } catch (error: any) {
      console.error("Failed to add reply:", error);
      toast.error("Failed to add reply");
    }
  };

  const handleEdit = async (commentId: string, content: string, mentions: string[]) => {
    try {
      const { error } = await supabase
        .from("comments")
        .update({
          content: content.trim(),
          mentions: mentions.length > 0 ? mentions : null,
          updated_at: new Date().toISOString(),
        })
        .eq("id", commentId);

      if (error) throw error;
      toast.success("Comment updated");
    } catch (error: any) {
      console.error("Failed to update comment:", error);
      toast.error("Failed to update comment");
    }
  };

  const handleDelete = async (commentId: string) => {
    try {
      const { error } = await supabase
        .from("comments")
        .delete()
        .eq("id", commentId);

      if (error) throw error;
      toast.success("Comment deleted");
    } catch (error: any) {
      console.error("Failed to delete comment:", error);
      toast.error("Failed to delete comment");
    }
  };

  const notifyMentions = async (mentionedUserIds: string[]) => {
    // Create notifications for mentioned users
    try {
      const notifications = mentionedUserIds.map((userId) => ({
        user_id: userId,
        type: "mention",
        title: "You were mentioned in a comment",
        message: `${user?.email || "Someone"} mentioned you in a comment`,
        action_url: `/record/${recordId}`,
      }));

      await supabase.from("agent_notifications").insert(notifications);
    } catch (error) {
      console.error("Failed to send mention notifications:", error);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center p-8">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full">
      {/* Comments list */}
      <ScrollArea className="flex-1">
        {comments.length === 0 ? (
          <div className="flex flex-col items-center justify-center p-8 text-center">
            <MessageSquare className="h-12 w-12 text-muted-foreground/30 mb-3" />
            <p className="text-muted-foreground">No comments yet</p>
            <p className="text-sm text-muted-foreground/70">
              Be the first to add a comment
            </p>
          </div>
        ) : (
          <div className="divide-y">
            {comments.map((comment) => (
              <CommentThread
                key={comment.id}
                comment={comment}
                currentUserId={user?.id || ""}
                workspaceId={workspaceId}
                onReply={handleReply}
                onEdit={handleEdit}
                onDelete={handleDelete}
              />
            ))}
          </div>
        )}
      </ScrollArea>

      {/* New comment input */}
      <div className="border-t p-4 space-y-3">
        <MentionInput
          value={newComment}
          onChange={(value, mentions) => {
            setNewComment(value);
            setNewMentions(mentions);
          }}
          onSubmit={handleSubmit}
          workspaceId={workspaceId}
          disabled={submitting}
          placeholder="Add a comment... Use @ to mention someone"
          rows={3}
        />
        <div className="flex justify-end">
          <Button
            onClick={handleSubmit}
            disabled={!newComment.trim() || submitting}
          >
            {submitting ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                Posting...
              </>
            ) : (
              "Post Comment"
            )}
          </Button>
        </div>
      </div>
    </div>
  );
};
